/**
 * v-resizable-columns
 *
 * Adds a drag handle to the right edge of every <th> of a Vuetify v-data-table
 * (or any plain <table>) so the user can resize columns. Long cell values are
 * truncated with an ellipsis (and get a hover tooltip), and double-clicking a
 * handle auto-fits the column to its content.
 *
 * Usage:
 *   <v-data-table v-resizable-columns />                 (auto key = route path)
 *   <v-data-table v-resizable-columns="'estimates'" />   (explicit storage key)
 *
 * Design notes:
 *   - Truncation is OFF until the table is "frozen" (first drag / auto-fit / a
 *     stored width being re-applied). That means dropping this directive onto an
 *     existing table does NOT change how it looks until the user resizes — safe
 *     to apply app-wide.
 *   - `table-layout: fixed` + explicit px widths on each <th> is what makes the
 *     widths authoritative so cells clip instead of expanding.
 *   - All CSS is injected once (self-contained), and drag handling is bound to
 *     the directive root via delegation so it survives Vuetify re-renders.
 */

// The narrowest a column may be dragged.
//
// This was 48px, which is wider than several real columns: Projects' "Key" is a
// 56px colour dot, so it had EIGHT pixels of travel before the drag stopped
// dead. That reads as a broken resizer rather than a floor, and it is the
// narrow icon columns — the ones most worth shrinking — that hit it first.
//
// 28px keeps a column wider than its own resize handle (22px, itself capped at
// 40% of the column) so the header stays grabbable, while leaving room for a
// dot or an icon.
const MIN_WIDTH = 28;
const MAX_AUTOFIT = 640; // don't let auto-fit create an absurdly wide column
const STYLE_ID = 'rc-injected-styles';

const GRIP_SVG =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cg fill='%23000'%3E%3Ccircle cx='9' cy='5' r='1.6'/%3E%3Ccircle cx='9' cy='12' r='1.6'/%3E%3Ccircle cx='9' cy='19' r='1.6'/%3E%3Ccircle cx='15' cy='5' r='1.6'/%3E%3Ccircle cx='15' cy='12' r='1.6'/%3E%3Ccircle cx='15' cy='19' r='1.6'/%3E%3C/g%3E%3C/svg%3E";

function injectStyles() {
    if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
/* Positioning context for the handle. This MUST be driven off the element
   itself, never off a class we add in JS: Vue patches an element's class from
   its vnode on every re-render, so a JS-added class is silently dropped the
   first time the table sorts. The th then falls back to position: static, the
   absolutely-positioned handle escapes to a different containing block, and the
   handles look like they vanished. */
.rc-table thead th { position: relative; }
/* The grab target for drag-to-resize and double-click-to-fit.
   WIDTH IS A HIT TARGET, not decoration. At 16px this was easy to miss, and a
   miss is not harmless: the click lands on the header instead, which SORTS.
   Sorting re-fetches and Vuetify rebuilds the <th> nodes, so the second click
   of a double-click lands on a brand-new element — and a dblclick event only
   fires when both clicks hit the SAME element. Missing the handle therefore
   does not just fail to auto-fit, it makes auto-fit impossible to trigger at
   all until the table settles. The visible grip stays 12px; only the
   catchment grows.
   z-index has to clear the header's own content: this app forces the sort icon
   visible and pushes it to the right edge (see the .projects-table rules in
   Jobs/index.vue), which is exactly where this sits. */
.col-resize-handle {
    position: absolute; top: 0; right: 0; width: 22px; max-width: 40%; height: 100%;
    display: flex; align-items: center; justify-content: center;
    cursor: col-resize; z-index: 5; touch-action: none; user-select: none;
    color: rgba(var(--v-theme-on-surface), 0.3);
}
/* A frozen th clips its children so long values ellipsise. The handle is a
   direct child div, so it is caught by that rule too — exempt it, or the grip
   can be trimmed on a narrow column. */
.rc-frozen.rc-table th .col-resize-handle { overflow: visible; max-width: 40%; }
.col-resize-handle::after {
    content: ''; width: 12px; height: 18px;
    background-color: currentColor; transition: color 0.15s ease;
    -webkit-mask: url("${GRIP_SVG}") center / contain no-repeat;
    mask: url("${GRIP_SVG}") center / contain no-repeat;
}
.col-resize-handle:hover, .col-resize-handle.is-dragging { color: rgb(var(--v-theme-primary)); }

/* Never let a text field inside a frozen cell be scrolled into view by focus.
   Vuetify's input sits in its own overflow context, and clicking it would
   otherwise nudge the cell. */
.rc-frozen.rc-table td .v-field__input, .rc-frozen.rc-table th .v-field__input,
.rc-frozen.rc-table td .v-field, .rc-frozen.rc-table th .v-field {
    overflow: hidden; overflow: clip;
}

/* Truncation only once the table is frozen (i.e. the user has resized).

   Note it is overflow: clip, NOT overflow: hidden. A hidden box is still a
   scroll container — it just has no visible scrollbar — so the moment you click
   an input the browser scrolls that box to bring the focused field into view,
   and the cell's contents shift sideways and stay there. On the Costings table,
   where every cell becomes a text field in edit mode, that reads as the table
   moving every time you click. clip produces no scroll container at all, so
   there is nothing for focus to scroll, and it still ellipsises. The hidden
   declaration before it is the fallback for browsers without clip.

   (Backticks are banned in here: this whole stylesheet is a JS template
   literal, and one would end the string.) */
.rc-frozen.rc-table th, .rc-frozen.rc-table td {
    overflow: hidden; overflow: clip; text-overflow: ellipsis; white-space: nowrap;
}
/* Cell values are rarely bare text — they sit in a flex wrapper (avatar + name,
   icon + client, …). Clipping only the td does nothing, because the inner
   element keeps its full intrinsic width and simply overflows. Every descendant
   needs min-width: 0 to be allowed to shrink below its content, and the text
   nodes themselves need the ellipsis. */
.rc-frozen.rc-table td *, .rc-frozen.rc-table th * { min-width: 0; }
.rc-frozen.rc-table td > *, .rc-frozen.rc-table th > * { max-width: 100%; }
.rc-frozen.rc-table td div, .rc-frozen.rc-table td span,
.rc-frozen.rc-table th div, .rc-frozen.rc-table th span {
    overflow: hidden; overflow: clip; text-overflow: ellipsis; white-space: nowrap;
}
/* …but never shrink or clip the fixed-size furniture (avatars, icons, chips,
   buttons), or a narrow column would squash them into slivers. */
.rc-frozen.rc-table td .v-avatar, .rc-frozen.rc-table td .v-chip,
.rc-frozen.rc-table td .v-btn, .rc-frozen.rc-table td svg,
.rc-frozen.rc-table td .action-icon-btn { flex-shrink: 0; overflow: visible; }
/* The list above is all Vuetify furniture, which is why it missed the app's
   OWN fixed-size cell contents. The Projects table builds its avatars, colour
   key and status toggle out of plain divs, so min-width: 0 above let them
   shrink to nothing: the Managers column rendered empty and the colour key
   became a sliver the moment the table froze. Any cell element whose size is
   the point, rather than a container for text, belongs here. */
.rc-frozen.rc-table td .manager-avatar, .rc-frozen.rc-table td .managers-avatars,
.rc-frozen.rc-table td .color-key-indicator, .rc-frozen.rc-table td .status-toggle,
.rc-frozen.rc-table td .status-toggle-wrapper, .rc-frozen.rc-table td .timeline-container
{ flex-shrink: 0; overflow: visible; }
.rc-frozen.rc-table td .manager-avatar { min-width: 32px; }
.rc-frozen.rc-table td .color-key-indicator { min-width: 100%; }
.rc-frozen.rc-table td .v-chip span { overflow: visible; }
`;
    document.head.appendChild(style);
}

function getTable(el) {
    // The directive is placed on the v-data-table root; the real <table> is nested.
    return el.matches('table') ? el : el.querySelector('table');
}

// The columns are defined by the FIRST header row only.
//
// Some tables (the estimate Costings P/L summary, for one) put every data row in
// the <thead> as <th>s, so `thead th` returns rows × columns cells for a table
// that only has two columns. Treating each of those as its own column made
// syncTableWidth sum ~6× the real width, and under `table-layout: fixed` that
// leftover got redistributed into the two actual columns — so the very first
// mousemove flung the column to the far end. `table-layout: fixed` sizes columns
// from the first row anyway, so that row is the only correct source of truth.
function getHeaderCells(table) {
    const firstRow = table.querySelector('thead > tr');
    return firstRow ? Array.from(firstRow.children).filter((c) => c.tagName === 'TH') : [];
}

// Instances that share a storage key share their widths, live.
//
// The estimate Costings page renders one <v-table> per category accordion and
// gives every one of them the same key. Each directive instance kept its widths
// in a private closure, so resizing category 3 left categories 1, 2 and 4 on
// their old widths until a full reload — and whichever one you dragged next
// overwrote the stored value with its own stale set. Registering here lets one
// drag update every live table that claims the same key.
const registry = new Map(); // key -> Set<(widths) => void>

function subscribe(key, fn) {
    if (!key) return () => {};
    let set = registry.get(key);
    if (!set) registry.set(key, (set = new Set()));
    set.add(fn);
    return () => {
        set.delete(fn);
        if (!set.size) registry.delete(key);
    };
}

function broadcast(key, widths, self) {
    const set = registry.get(key);
    if (!set) return;
    set.forEach((fn) => {
        if (fn !== self) fn(widths);
    });
}

function readStored(key) {
    if (!key) return null;
    try {
        const raw = localStorage.getItem('resizable-cols:' + key);
        const arr = raw ? JSON.parse(raw) : null;
        return Array.isArray(arr) ? arr : null;
    } catch (e) {
        return null;
    }
}

function writeStored(key, widths) {
    if (!key) return;
    try {
        localStorage.setItem('resizable-cols:' + key, JSON.stringify(widths));
    } catch (e) {
        /* ignore quota / privacy-mode errors */
    }
}

// A column hidden with v-show generates no boxes at all (display: none).
// It still occupies a slot in the widths array — see currentWidths — but it must
// never be given a width or counted in the table total: doing so made the table
// wider than the columns it actually renders, and `table-layout: fixed` handed
// that difference straight back out across every visible column.
function isRendered(cell) {
    return cell.offsetWidth > 0 || cell.offsetHeight > 0;
}

// Pin a column to an exact width.
//
// Setting `width` alone is not enough: Vuetify emits an inline `min-width` on
// the th from the column definition (headers[].width), and min-width beats
// width. That is why the handle used to stop dead at the text — the column was
// floored at its declared min-width and simply would not go left past the
// letters. Writing all three makes the drag authoritative.
function setWidth(th, w, cap = true) {
    th.style.width = w + 'px';
    th.style.minWidth = w + 'px';
    // `cap` is false for the DEFAULT widths in fill mode. A max-width there
    // would defeat the whole point: the table is allowed to grow to the width
    // of its container, and a capped column cannot take its share of the
    // surplus — the table would stretch while the columns stayed put, leaving
    // the gap beside Actions that fill mode exists to remove.
    th.style.maxWidth = cap ? w + 'px' : '';
}

// The table's own width must equal the sum of its columns.
//
// This is the whole reason a column could not be dragged narrower than its text.
// `table-layout: fixed` does NOT mean "columns get exactly the width I asked
// for" — it means the browser lays columns out from the declared widths and then
// distributes any leftover table width proportionally across them. With the
// table left at `auto` it still filled its container, so that leftover was
// always large: a column set to 379px rendered at 503px, and dragging left just
// handed the freed space straight back to the same column. Sizing the table to
// the exact sum leaves nothing to redistribute, so a declared width is the width
// you get — and the column can shrink until the value clips.
function syncTableWidth(table, fill) {
    const total = getHeaderCells(table).reduce(
        (sum, th) =>
            isRendered(th) ? sum + (parseFloat(th.style.width) || th.offsetWidth) : sum,
        0,
    );
    if (total <= 0) return;

    // FILL MODE — the DEFAULT widths, before the user has resized anything.
    //
    // Pinning the table to the exact sum here was wrong: on a screen wider than
    // the columns it left a band of dead space to the right of Actions, and it
    // held every column at its declared width, so headers that only just fit
    // ellipsised ("Contract …", "Creat…", "K…") on a display with room to spare.
    //
    // `width: 100%` with the sum as a FLOOR fixes both. Under `table-layout:
    // fixed` the browser shares any surplus across the columns in proportion,
    // so the table fills the screen, the labels fit, and Actions still finishes
    // at the right-hand edge — which is all "don't push the buttons too far
    // right" ever asked for. Below the floor it scrolls, with every column at
    // its declared width.
    if (fill) {
        table.style.width = '100%';
        table.style.minWidth = total + 'px';
        table.style.maxWidth = '';
        return;
    }
    // Setting `width` alone is not enough. A page stylesheet can put a floor
    // under the table with its own `min-width` (the estimate Costings table
    // carries `min-width: 1400px`), and min-width beats width. Below that floor
    // the table stayed wide, so `table-layout: fixed` again had surplus to
    // redistribute across every column: dragging one edge nudged all the other
    // columns and the dragged edge would not follow the pointer. Pinning all
    // three leaves nothing to redistribute at any size.
    table.style.width = total + 'px';
    table.style.minWidth = total + 'px';
    table.style.maxWidth = total + 'px';
}

// Freeze the table to fixed layout with explicit px widths so a single column
// resize does not reflow every other column. Also enables truncation.
function freeze(table, widths, fill) {
    const ths = getHeaderCells(table);
    const measured = widths || ths.map((th) => th.offsetWidth);
    table.classList.add('rc-frozen');
    table.style.tableLayout = 'fixed';
    ths.forEach((th, i) => {
        if (measured[i] && isRendered(th)) setWidth(th, measured[i], !fill);
    });
    syncTableWidth(table, fill);
}

// The widths the headers themselves declare, for `fitDeclared`.
//
// Falls back to the rendered width for any column that declares none, so a
// table that misses one header still lays out sensibly instead of collapsing
// that column to zero — `freeze` skips falsy entries, and a zero here would
// leave the column unsized while the table is pinned to the sum of the others.
function declaredWidths(ths) {
    return ths.map((th) => parseFloat(th.style.width) || th.offsetWidth || 0);
}

// A column hidden with v-show (display: none) measures 0. Recording that zero
// would collapse the column the moment it is switched back on: the table is
// pinned to the sum of the *other* columns, so fixed layout has nothing left to
// hand it. Keep the width we already had for any column that isn't rendered.
function currentWidths(table, previous) {
    return getHeaderCells(table).map((th, i) => {
        const w = Math.round(th.offsetWidth);
        return w > 0 ? w : (previous && previous[i]) || 0;
    });
}

// Measure a cell's natural (untruncated) content width using an off-DOM clone,
// so we never disturb the live table layout (no flicker).
function measureCell(cell) {
    const clone = cell.cloneNode(true);
    const handle = clone.querySelector && clone.querySelector('.col-resize-handle');
    if (handle) handle.remove();
    const cs = getComputedStyle(cell);
    Object.assign(clone.style, {
        position: 'absolute',
        left: '-99999px',
        top: '0',
        width: 'auto',
        maxWidth: 'none',
        whiteSpace: 'nowrap',
        overflow: 'visible',
        display: 'inline-block',
        boxSizing: 'border-box',
        font: cs.font,
        fontSize: cs.fontSize,
        fontFamily: cs.fontFamily,
        fontWeight: cs.fontWeight,
        letterSpacing: cs.letterSpacing,
        paddingLeft: cs.paddingLeft,
        paddingRight: cs.paddingRight
    });
    document.body.appendChild(clone);
    const w = clone.getBoundingClientRect().width;
    clone.remove();
    return Math.ceil(w);
}

// Double-click auto-fit: size the column to fully show its widest content.
function autofit(table, th, previous) {
    const ths = getHeaderCells(table);
    const colIndex = ths.indexOf(th);
    if (colIndex < 0) return;

    freeze(table); // pin the other columns at their current widths first

    // Body rows, plus any header rows below the first — tables that render their
    // data as <th>s in the <thead> would otherwise auto-fit to the header alone.
    const rows = Array.from(table.querySelectorAll('tbody tr, thead > tr:not(:first-child)')).slice(0, 200);
    let widest = measureCell(th);
    rows.forEach((row) => {
        const cell = row.children[colIndex];
        if (cell) widest = Math.max(widest, measureCell(cell));
    });

    const target = Math.min(MAX_AUTOFIT, Math.max(MIN_WIDTH, widest + 2));
    setWidth(th, target);
    syncTableWidth(table);
    return currentWidths(table, previous);
}

export const resizableColumns = {
    mounted(el, binding) {
        injectStyles();
        // Binding accepts a bare key (the original form) or an options object:
        //   v-resizable-columns="'estimates'"
        //   v-resizable-columns="{ key: 'projects', fitDeclared: true }"
        const options = binding.value && typeof binding.value === 'object' ? binding.value : { key: binding.value };
        // Explicit key, else persist per route so widths survive a reload.
        const key = options.key || (typeof location !== 'undefined' ? location.pathname : '');
        // Opt-in: freeze to the widths the HEADERS declare, on first render,
        // before the user has resized anything.
        //
        // Without it a table is laid out `auto`, so a declared width is only a
        // suggestion — one long value (a 47-character project name, a timeline
        // with a progress bar) stretches its column and shoves every column
        // after it, which is how the Projects table pushed its action buttons
        // far off to the right. Freezing makes the declared widths the widths,
        // and syncTableWidth pins the table to their exact sum so there is no
        // slack left to redistribute.
        //
        // Deliberately opt-in rather than "freeze whenever widths are declared":
        // plenty of tables set a width on one or two columns and mean it only as
        // a hint, and silently pinning those would change every one of them.
        const fitDeclared = options.fitDeclared === true;
        // Keep widths in directive-owned memory as the immediate source of
        // truth. localStorage adds reload persistence, but input re-renders must
        // not depend on storage being available.
        let stableWidths = readStored(key);

        // Single place that owns a width change: remember it, persist it, and
        // tell the sibling tables that share this key.
        const commit = (widths) => {
            if (!widths) return;
            stableWidths = widths;
            writeStored(key, widths);
            broadcast(key, widths, receive);
        };
        // A sibling resized. Adopt its widths without echoing back.
        const receive = (widths) => {
            stableWidths = widths;
            setup();
        };

        // Ensure every header has a (purely visual) drag handle, and re-apply
        // stored widths. Runs on mount and after every Vuetify re-render.
        const setup = () => {
            const table = getTable(el);
            if (!table) return;
            table.classList.add('rc-table');

            const ths = getHeaderCells(table);
            if (stableWidths && stableWidths.length === ths.length) {
                // A width the user set themselves always wins over the default.
                freeze(table, stableWidths);
            } else if (fitDeclared) {
                // fill: these are DEFAULTS, so let the table use the whole
                // width available rather than leaving dead space beside it.
                freeze(table, declaredWidths(ths), true);
            }

            ths.forEach((th) => {
                if (th.querySelector('.col-resize-handle')) return; // already present
                const handle = document.createElement('div');
                handle.className = 'col-resize-handle';
                handle.setAttribute('aria-hidden', 'true');
                handle.title = 'Drag to resize · double-click to fit';
                th.appendChild(handle);
            });
        };

        // Drag state + handlers are bound ONCE to the directive's root element
        // (which Vuetify never replaces) using event delegation. Binding to the
        // handles directly breaks after the first resize, because Vuetify tears
        // down and rebuilds the <th> nodes — and their listeners — on re-render.
        let drag = null;

        const onMove = (e) => {
            if (!drag) return;
            const w = Math.max(MIN_WIDTH, drag.startW + (e.clientX - drag.startX));
            setWidth(drag.th, w);
            syncTableWidth(drag.table); // keep the table exactly as wide as its columns
        };
        const onUp = () => {
            if (!drag) return;
            drag.handle.classList.remove('is-dragging');
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            const table = getTable(el);
            if (table) {
                commit(currentWidths(table, stableWidths));
            }
            drag = null;
        };
        const onDown = (e) => {
            const handle = e.target.closest && e.target.closest('.col-resize-handle');
            if (!handle) return;
            const table = getTable(el);
            const th = handle.parentElement;
            if (!table || !th) return;

            e.preventDefault();
            e.stopPropagation(); // don't let the header start a column sort

            freeze(table); // pin all columns at their current widths first
            drag = { table, th, handle, startX: e.clientX, startW: th.offsetWidth };
            handle.classList.add('is-dragging');
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
            window.addEventListener('pointermove', onMove);
            window.addEventListener('pointerup', onUp);
        };
        // Swallow the click that follows a resize so it can't trigger sort.
        const onClick = (e) => {
            if (e.target.closest && e.target.closest('.col-resize-handle')) {
                e.stopPropagation();
                e.preventDefault();
            }
        };
        // Double-click a handle → auto-fit that column to its content.
        const onDblClick = (e) => {
            const handle = e.target.closest && e.target.closest('.col-resize-handle');
            if (!handle) return;
            e.preventDefault();
            e.stopPropagation();
            const table = getTable(el);
            const th = handle.parentElement;
            if (table && th) commit(autofit(table, th, stableWidths));
        };
        // Add a native tooltip to any cell whose content is actually truncated.
        //
        // The overflow has to be looked for in the cell's descendants, not just
        // the cell: the value usually sits in a <span> inside a flex wrapper, so
        // it's that span that ellipsises while the td itself reports no overflow
        // at all. Checking only the td meant truncated values never got a
        // tooltip — the exact case where you most need one.
        const isClipped = (node) => node.scrollWidth > node.clientWidth + 1;
        const onOver = (e) => {
            const cell = e.target.closest && e.target.closest('td, th');
            if (!cell || !el.contains(cell) || cell.classList.contains('col-resize-handle')) return;

            const clipped =
                isClipped(cell) ||
                Array.prototype.some.call(cell.querySelectorAll('*'), isClipped);

            if (clipped) {
                const text = (cell.textContent || '').trim();
                if (text && cell.getAttribute('title') !== text) cell.setAttribute('title', text);
            } else if (cell.hasAttribute('title')) {
                cell.removeAttribute('title');
            }
        };

        // Keep the mouse/touch events that shadow our pointer events away from
        // ancestors that implement click-and-drag panning.
        //
        // The Costings table sits inside a `.table-scroll-wrapper` whose
        // @mousedown starts a pan. Cancelling a *pointer*down does not suppress
        // the mousedown that follows it for a mouse pointer, and stopping the
        // pointer event's propagation does nothing for the separately dispatched
        // mouse event — so grabbing a resize handle also started a pan, and the
        // table slid sideways under the cursor at 2× the drag while the column
        // resized at 1×. Swallowing these in the capture phase on our own root
        // means the event never reaches the ancestor at all, on any page.
        const swallow = (e) => {
            if (e.target.closest && e.target.closest('.col-resize-handle')) {
                e.stopPropagation();
                if (e.cancelable) e.preventDefault();
            }
        };

        // Capture phase so we intercept before Vuetify's own header handlers.
        el.addEventListener('pointerdown', onDown, true);
        el.addEventListener('mousedown', swallow, true);
        el.addEventListener('touchstart', swallow, { capture: true, passive: false });
        el.addEventListener('click', onClick, true);
        el.addEventListener('dblclick', onDblClick, true);
        el.addEventListener('mouseover', onOver);

        // Wait for Vuetify to render the table, then add the handles.
        requestAnimationFrame(setup);

        // Vue can reset a header's inline width without replacing the header
        // node (for example when a Costings row switches into edit mode or an
        // input updates). Watch style attributes as well as child replacement,
        // then re-apply the persisted user widths. Ignore mutations during an
        // active drag so the observer never fights the pointer handler.
        const observerOptions = {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style']
        };
        const observer = new MutationObserver(() => {
            if (drag) return;
            observer.disconnect();
            setup();
            observer.observe(el, observerOptions);
        });
        observer.observe(el, observerOptions);

        const unsubscribe = subscribe(key, receive);

        el._resizeCleanup = () => {
            unsubscribe();
            observer.disconnect();
            el.removeEventListener('pointerdown', onDown, true);
            el.removeEventListener('mousedown', swallow, true);
            el.removeEventListener('touchstart', swallow, { capture: true });
            el.removeEventListener('click', onClick, true);
            el.removeEventListener('dblclick', onDblClick, true);
            el.removeEventListener('mouseover', onOver);
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };
    },

    unmounted(el) {
        if (el._resizeCleanup) el._resizeCleanup();
        el._resizeCleanup = null;
    },
};

export default resizableColumns;
